import * as React from "react";
import { useState } from "react";
import { Button, ButtonToolbar, DeleteMenuItem, MenuItem } from "components/bootstrap";
import { ConfirmDialog, IfPermitted, LinkContainer } from "components/common";
import Routes from "routing/Routes";
import HideOnCloud from "util/conditional/HideOnCloud";
import { isInputInSetupMode, isInputRunning } from "components/inputs/helpers/inputState";
import recentMessagesTimeRange from "util/TimeRangeHelper";
import { getPathnameWithoutId } from "util/URLUtils";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useLocation from "routing/useLocation";
import useFeature from "hooks/useFeature";
import InputStatesStore from "stores/inputs/InputStatesStore";
import { MoreActions } from "components/common/EntityDataTable";
import useInputsStates from "hooks/useInputsStates";
import useInputMutations from "hooks/useInputMutations";
import { INPUT_SETUP_MODE_FEATURE_FLAG, InputSetupWizard } from "components/inputs/InputSetupWizard";
import { InputForm, InputStateControl, StaticFieldForm } from "components/inputs";
const FORWARDER_SERVICE_INPUT = "org.graylog.plugins.forwarder.input.ForwarderServiceInput";
const GL2_FORWARDER_INPUT = "gl2_forwarder_input";
const GL2_SOURCE_INPUT = "gl2_source_input";
const InputsActions = ({ input, inputTypes: _, inputTypeDescriptions, currentNode }) => {
  var _a;
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const [showStaticFieldForm, setShowStaticFieldForm] = useState(false);
  const [showConfigurationForm, setShowConfigurationForm] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const { data: inputStates, isLoading: isLoadingInputStates } = useInputsStates();
  const { updateInput, deleteInput } = useInputMutations();
  const sendTelemetry = useSendTelemetry();
  const { pathname } = useLocation();
  const inputSetupFeatureFlagIsEnabled = useFeature(INPUT_SETUP_MODE_FEATURE_FLAG);
  const openWizard = () => {
    setShowWizard(true);
  };
  const closeWizard = () => {
    setShowWizard(false);
  };
  const onDeleteInput = () => {
    setShowConfirmDeleteDialog(true);
  };
  const editInput = () => {
    setShowConfigurationForm(true);
    sendTelemetry(TELEMETRY_EVENT_TYPE.INPUTS.INPUT_EDIT_CLICKED, {
      app_pathname: getPathnameWithoutId(pathname),
      app_action_value: "show-received-messages"
    });
  };
  const hanleInputUpdate = async (inputData) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.INPUTS.INPUT_UPDATED, {
      app_pathname: getPathnameWithoutId(pathname),
      app_action_value: "input-edit"
    });
    await updateInput({ input: inputData, inputId: input.id });
  };
  const enterInputSetupMode = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.INPUTS.INPUT_SETUP_ENTERED, {
      app_pathname: getPathnameWithoutId(pathname),
      app_action_value: "input-enter-setup"
    });
    InputStatesStore.setup(input);
  };
  const exitInputSetupMode = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.INPUTS.INPUT_SETUP_EXITED, {
      app_pathname: getPathnameWithoutId(pathname),
      app_action_value: "input-exit-setup"
    });
    InputStatesStore.stop(input);
  };
  const handleConfirmDelete = async () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.INPUTS.INPUT_DELETED, {
      app_pathname: getPathnameWithoutId(pathname),
      app_action_value: "input-delete"
    });
    await deleteInput({ inputId: input.id });
  };
  const cancelDelete = () => {
    setShowConfirmDeleteDialog(false);
  };
  const definition = inputTypeDescriptions[input.type];
  const queryField = input.type === FORWARDER_SERVICE_INPUT ? GL2_FORWARDER_INPUT : GL2_SOURCE_INPUT;
  return /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: ["searches:relative"] }, /* @__PURE__ */ React.createElement(
    LinkContainer,
    {
      key: `received-messages-${input.id}`,
      to: Routes.search(`${queryField}:${input.id}`, recentMessagesTimeRange())
    },
    /* @__PURE__ */ React.createElement(
      Button,
      {
        bsSize: "xsmall",
        onClick: () => {
          sendTelemetry(TELEMETRY_EVENT_TYPE.INPUTS.SHOW_RECEIVED_MESSAGES_CLICKED, {
            app_pathname: getPathnameWithoutId(pathname),
            app_action_value: "show-received-messages"
          });
        }
      },
      "Received messages"
    )
  )), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: `inputs:changestate:${input.id}` }, !isLoadingInputStates && /* @__PURE__ */ React.createElement(
    InputStateControl,
    {
      key: `input-state-control-${input.id}`,
      input,
      openWizard,
      inputStates
    }
  )), /* @__PURE__ */ React.createElement(MoreActions, null, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: [`inputs:edit:${input.id}`, `input_types:create:${input.type}`] }, /* @__PURE__ */ React.createElement(MenuItem, { key: `edit-input-${input.id}`, onSelect: editInput, disabled: definition === void 0 }, "Edit input"), /* @__PURE__ */ React.createElement(HideOnCloud, null, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: [`inputs:edit:${input.id}`, `input_types:create:${input.type}`] }, /* @__PURE__ */ React.createElement(
    LinkContainer,
    {
      key: `manage-extractors-${input.id}`,
      to: input.global ? Routes.global_input_extractors(input.id) : Routes.local_input_extractors((_a = currentNode == null ? void 0 : currentNode.node) == null ? void 0 : _a.node_id, input.id)
    },
    /* @__PURE__ */ React.createElement(
      MenuItem,
      {
        onClick: () => {
          sendTelemetry(TELEMETRY_EVENT_TYPE.INPUTS.MANAGE_EXTRACTORS_CLICKED, {
            app_pathname: getPathnameWithoutId(pathname),
            app_action_value: "manage-extractors"
          });
        }
      },
      "Manage extractors"
    )
  ))), /* @__PURE__ */ React.createElement(LinkContainer, { to: Routes.SYSTEM.INPUT_DIAGNOSIS(input.id) }, /* @__PURE__ */ React.createElement(
    MenuItem,
    {
      key: `input-diagnosis-${input.id}`,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.INPUTS.INPUT_DIAGNOSIS_CLICKED, {
          app_pathname: getPathnameWithoutId(pathname),
          app_action_value: "input-diagnosis"
        });
      }
    },
    "Input Diagnosis"
  )), inputSetupFeatureFlagIsEnabled && (isInputInSetupMode(inputStates, input.id) ? /* @__PURE__ */ React.createElement(
    MenuItem,
    {
      key: `remove-setup-mode-${input.id}`,
      onSelect: exitInputSetupMode,
      disabled: definition === void 0
    },
    "Exit Setup mode"
  ) : !isInputRunning(inputStates, input.id) && /* @__PURE__ */ React.createElement(
    MenuItem,
    {
      key: `setup-mode-${input.id}`,
      onSelect: enterInputSetupMode,
      disabled: definition === void 0
    },
    "Enter Setup mode"
  ))), input.global && input.node && /* @__PURE__ */ React.createElement(LinkContainer, { to: Routes.filtered_metrics(input.node, input.id) }, /* @__PURE__ */ React.createElement(
    MenuItem,
    {
      key: `show-metrics-${input.id}`,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.INPUTS.SHOW_METRICS_CLICKED, {
          app_pathname: getPathnameWithoutId(pathname),
          app_action_value: "show-metrics"
        });
      }
    },
    "Show metrics"
  )), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: [`inputs:edit:${input.id}`, `input_types:create:${input.type}`] }, /* @__PURE__ */ React.createElement(
    MenuItem,
    {
      key: `add-static-field-${input.id}`,
      onSelect: () => {
        setShowStaticFieldForm(true);
      }
    },
    "Add static field"
  )), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: ["inputs:terminate", `input_types:create:${input.type}`] }, /* @__PURE__ */ React.createElement(MenuItem, { key: `divider-${input.id}`, divider: true })), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: ["inputs:terminate", `input_types:create:${input.type}`] }, /* @__PURE__ */ React.createElement(DeleteMenuItem, { key: `delete-input-${input.id}`, onSelect: onDeleteInput }, "Delete input"))), showConfirmDeleteDialog && /* @__PURE__ */ React.createElement(ConfirmDialog, { title: "Deleting Input", show: true, onConfirm: handleConfirmDelete, onCancel: cancelDelete }, "Do you really want to delete input ", input.title, "?"), inputSetupFeatureFlagIsEnabled && showWizard && /* @__PURE__ */ React.createElement(InputSetupWizard, { input, show: showWizard, onClose: closeWizard }), showStaticFieldForm && /* @__PURE__ */ React.createElement(StaticFieldForm, { input, setShowModal: setShowStaticFieldForm }), definition && showConfigurationForm && /* @__PURE__ */ React.createElement(
    InputForm,
    {
      setShowModal: setShowConfigurationForm,
      key: `edit-form-input-${input.id}`,
      globalValue: input.global,
      nodeValue: input.node,
      configFields: definition.requested_configuration,
      description: definition == null ? void 0 : definition.description,
      title: `Editing Input ${input.title}`,
      titleValue: input.title,
      typeName: input.type,
      includeTitleField: true,
      handleSubmit: hanleInputUpdate,
      submitButtonText: "Update input",
      values: input.attributes
    }
  ));
};
export default InputsActions;
